import classnames from 'classnames'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import DownloadIcon from '@material-symbols/svg-300/rounded/download.svg'

import CertificateValidity from '../../components/CertificateValidity/CertificateValidity'
import learningItemsWithCertificates from '../../state/calculations/learningItemsWithCertificates'
import { useUser } from '../../state/entities'
import useUserLearningItems from '../../state/queries/useUserLearningItems'

import styles from './InsightsPage.module.scss'

/**********************************************************************
 * InsightsPageCertificates
 **********************************************************************/
export const InsightsPageCertificates = React.memo(function InsightsPageCertificates({
  userUuid,
}: {
  userUuid?: string
}) {
  const { t } = useTranslation()

  const { user } = useUser(userUuid)

  const { userLearningItems } = useUserLearningItems(userUuid)

  const certificateItems = useMemo(() => learningItemsWithCertificates(userLearningItems), [userLearningItems])

  return (
    <section className={styles.InsightsPageCertificates}>
      <header>
        <h1>{t('certificate.certificates')}</h1>
      </header>

      <main className={classnames({ skeleton: user.isDummy })}>
        <table>
          <thead>
            <tr>
              <th className={styles.nameColumn}>{t('certificate.certificate name')}</th>
              <th className={styles.validityColumn}>{t('certificate.certificate validity')}</th>
              <th className={styles.statusColumn}>{t('certificate.certificate status')}</th>
              <th className={styles.downloadColumn}>{t('certificate.certificate download')}</th>
            </tr>
          </thead>
          <tbody>
            {certificateItems.map((learningItem) => {
              const certificate = learningItem.certificate!
              return (
                <tr key={learningItem.uuid}>
                  <td className={styles.nameColumn}>
                    <span className={styles.columnDescription}>{t('certificate.certificate name')}</span>
                    <span className={styles.columnContent}>{certificate.title}</span>
                  </td>
                  <td className={styles.validityColumn}>
                    <span className={styles.columnDescription}>{t('certificate.certificate validity')}</span>
                    {certificate.validUntil
                      ? t('format.DATE', { date: certificate.validUntil })
                      : t('certificate.unlimited validity')}
                  </td>
                  <td className={styles.statusColumn}>
                    <span className={styles.columnDescription}>{t('certificate.certificate status')}</span>
                    <CertificateValidity until={certificate.validUntil} />
                  </td>
                  <td className={styles.downloadColumn}>
                    {certificate.certificateUrl && (
                      <a target="_blank" href={certificate.certificateUrl}>
                        <DownloadIcon /> {t('certificate.download certificate')}
                      </a>
                    )}
                  </td>
                </tr>
              )
            })}
            {!certificateItems.length && (
              <tr>
                <td colSpan={4} className={styles.empty}>
                  {t('certificate.certificates empty')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </main>
    </section>
  )
})
